import React, { useState, useEffect, useRef } from 'react';
import { shippingApi } from '../../api/shippingApi';
import { allowsArrivalDateInput } from './shukkaDateRules';
import { areSplitPlansEqual } from './splitPlanUtils';

const SHIPPING_PREPARATION_OPTIONS = [
  { value: '', label: '選択' },
  { value: '済', label: '済' },
  { value: '入荷', label: '入荷' },
  { value: '工場在庫', label: '工場在庫' },
  { value: 'メーカー直送', label: '直送' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return num.toLocaleString();
};

const EMPTY_FORM = {
  planned_qty: 1,
  supplier_response_due_date: '',
  shipment_date: '',
  arrival_date: '',
  adjustment_memo: '',
  shipping_preparation: '',
};

const normalizePlansResponse = (plansResponse) => (
  Array.isArray(plansResponse) ? plansResponse : (plansResponse?.data || [])
);

export const HacchuSplitPlanInlineEditor = ({
  hacchuCode,
  hacchuChangecnt,
  hacchuDatacode,
  hacchuAmount,
  onDataChange,
  shukkaType = null,
  hideShipmentFields = false,  // 発送日と発送準備を非表示にするか（仕入先共有ページ用）
  hideSupplierResponseDueDate = false,  // 予定納期を非表示にするか（発送予定ページ・在庫状況ページ用）
  readOnly = false,  // true の場合は表示専用（分納一覧のみ）
  initialSplitPlans = undefined,  // 参照画面など一覧で既に取得済みの分納計画
}) => {
  const showArrivalDate = !hideShipmentFields && allowsArrivalDateInput(shukkaType);
  const hasInitialCache = initialSplitPlans !== undefined;
  const [splitPlans, setSplitPlans] = useState(() => (
    hasInitialCache && Array.isArray(initialSplitPlans) ? initialSplitPlans : []
  ));
  const [loading, setLoading] = useState(!hasInitialCache);
  const [loadError, setLoadError] = useState(null);
  const alertShownRef = useRef(false);
  const splitPlansRef = useRef(splitPlans);
  const onDataChangeRef = useRef(onDataChange);
  const seededKeyRef = useRef(null);
  const [editingSplitNo, setEditingSplitNo] = useState(null);
  const [addingNew, setAddingNew] = useState(() => !readOnly && hasInitialCache);
  const [formData, setFormData] = useState(() => (
    !readOnly && hasInitialCache ? { ...EMPTY_FORM } : {}
  ));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  splitPlansRef.current = splitPlans;
  onDataChangeRef.current = onDataChange;

  const loadData = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setLoadError(null);
    }
    try {
      const plansResponse = await shippingApi.getHacchuSplitPlans(hacchuCode, hacchuChangecnt, hacchuDatacode);
      const plans = normalizePlansResponse(plansResponse);
      const unchanged = silent && areSplitPlansEqual(splitPlansRef.current, plans);
      if (!unchanged) {
        setSplitPlans(plans);
        if (onDataChangeRef.current) {
          onDataChangeRef.current(plans);
        }
      }

      if (!readOnly && !editingSplitNo && !addingNew) {
        setAddingNew(true);
        setFormData({ ...EMPTY_FORM });
        setErrors({});
      }
    } catch (error) {
      console.error('データ取得エラー:', error);
      const message = error?.message || 'データの取得に失敗しました';
      if (silent) {
        return;
      }
      setLoadError(message);
      if (!readOnly) {
        if (!alertShownRef.current) {
          alertShownRef.current = true;
          alert(message);
        }
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const key = `${hacchuCode}-${hacchuChangecnt}-${hacchuDatacode}`;
    if (initialSplitPlans !== undefined) {
      if (seededKeyRef.current !== key) {
        seededKeyRef.current = key;
        setSplitPlans(Array.isArray(initialSplitPlans) ? initialSplitPlans : []);
        setLoadError(null);
        setLoading(false);
        if (!readOnly) {
          setAddingNew(true);
          setFormData({ ...EMPTY_FORM });
          setErrors({});
        }
      }
      loadData({ silent: true });
      return;
    }
    seededKeyRef.current = key;
    loadData({ silent: false });
    // 明細キー変化時のみ再取得。initialSplitPlans 参照は依存に入れない（親 setItems とのループ防止）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hacchuCode, hacchuChangecnt, hacchuDatacode]);

  const handleAdd = () => {
    if (readOnly) return;
    setAddingNew(true);
    setEditingSplitNo(null);
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const handleEdit = (plan) => {
    if (readOnly) return;
    setEditingSplitNo(plan.split_no);
    setAddingNew(false);
    setFormData({
      planned_qty: plan.planned_qty,
      supplier_response_due_date: formatDate(plan.supplier_response_due_date),
      shipment_date: formatDate(plan.shipment_date),
      arrival_date: formatDate(plan.arrival_date),
      adjustment_memo: plan.adjustment_memo || '',
      shipping_preparation: plan.shipping_preparation || '',
    });
    setErrors({});
  };

  const handleCancel = () => {
    if (readOnly) return;
    setEditingSplitNo(null);
    // 編集モードから戻った時も新規追加行を表示
    setAddingNew(true);
    setFormData({ ...EMPTY_FORM });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.planned_qty || formData.planned_qty <= 0) {
      newErrors.planned_qty = '分納数量は1以上である必要があります';
    }
    
    // 分納数量の合計チェック
    const existingTotal = splitPlans.reduce((sum, p) => {
      if (editingSplitNo && p.split_no === editingSplitNo) {
        return sum; // 編集中の分納は除外
      }
      return sum + p.planned_qty;
    }, 0);
    
    const newTotal = existingTotal + parseInt(formData.planned_qty || 0);
    if (newTotal > hacchuAmount) {
      newErrors.planned_qty = `分納数量の合計が発注数量を超えます。発注数量: ${hacchuAmount}, 合計: ${newTotal}`;
    }

    if (!hideShipmentFields && (!formData.shipment_date || formData.shipment_date.trim() === '')) {
      newErrors.shipment_date = '発送日は必須です';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!validate()) return;
    
    setSaving(true);
    try {
      const saveData = {
        planned_qty: parseInt(formData.planned_qty),
        ...(hideSupplierResponseDueDate ? {} : {
          supplier_response_due_date: formData.supplier_response_due_date || null,
        }),
        ...(hideShipmentFields ? {} : {
          shipment_date: formData.shipment_date || null,
          ...(showArrivalDate ? { arrival_date: formData.arrival_date || null } : { arrival_date: null }),
          shipping_preparation: formData.shipping_preparation || null,
        }),
        adjustment_memo: formData.adjustment_memo || null,
      };

      if (addingNew) {
        // 追加
        await shippingApi.createHacchuSplitPlan(hacchuCode, hacchuChangecnt, hacchuDatacode, saveData);
      } else {
        // 更新
        await shippingApi.updateHacchuSplitPlan(hacchuCode, hacchuChangecnt, hacchuDatacode, editingSplitNo, saveData);
      }
      
      const wasAddingNew = addingNew;
      // 保存後にデータを再取得
      try {
        const plansResponse = await shippingApi.getHacchuSplitPlans(hacchuCode, hacchuChangecnt, hacchuDatacode);
        const plans = normalizePlansResponse(plansResponse);
        setSplitPlans(plans);
        
        if (onDataChange) {
          onDataChange(plans);
        }
        
        setEditingSplitNo(null);
        setFormData({ ...EMPTY_FORM });
        setErrors({});
        setAddingNew(true);
      } catch (error) {
        console.error('データ取得エラー:', error);
        // エラーが発生しても続行
        setEditingSplitNo(null);
        if (wasAddingNew) {
          setAddingNew(true);
        }
      }
    } catch (error) {
      console.error('保存エラー:', error);
      const errorMessage = error.response?.data?.detail || error.message || '保存に失敗しました';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (splitNo) => {
    if (readOnly) return;
    if (!window.confirm('この分納計画を削除しますか？')) return;
    
    setSaving(true);
    try {
      await shippingApi.deleteHacchuSplitPlan(hacchuCode, hacchuChangecnt, hacchuDatacode, splitNo);
      await loadData();
    } catch (error) {
      console.error('削除エラー:', error);
      alert(error.response?.data?.detail || '削除に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // 予定納期と発送日/発送準備の排他制御（予定納期が非表示の場合は無効化）
      if (!hideSupplierResponseDueDate) {
        // 予定納期が入力されたら（空でない値が設定されたら）、発送日と発送準備をクリア
        if (field === 'supplier_response_due_date') {
          if (value && value.trim() !== '') {
            newData.shipment_date = '';
            newData.shipping_preparation = '';
          }
        }
      }
      // 発送日が入力されたら（空でない値が設定されたら）、予定納期をクリア
      if (field === 'shipment_date') {
        if (value && value.trim() !== '') {
          if (!hideSupplierResponseDueDate) {
            newData.supplier_response_due_date = '';
          }
        }
      }
      // 発送準備が入力されたら（空でない値が設定されたら）、予定納期をクリア
      if (field === 'shipping_preparation') {
        if (value && value.trim() !== '' && value !== '選択') {
          if (!hideSupplierResponseDueDate) {
            newData.supplier_response_due_date = '';
          }
        }
      }
      
      return newData;
    });
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // 予定納期が入力されているかチェック（予定納期が非表示の場合は常にfalse）
  const hasSupplierResponseDueDate = !hideSupplierResponseDueDate && 
    formData.supplier_response_due_date && formData.supplier_response_due_date.trim() !== '';
  
  // 発送日または発送準備が入力されているかチェック
  const hasShipmentData = (formData.shipment_date && formData.shipment_date.trim() !== '') || 
                          (formData.shipping_preparation && formData.shipping_preparation.trim() !== '');

  const splitTotalQty = splitPlans.reduce((sum, plan) => sum + plan.planned_qty, 0);

  if (loading) {
    return (
      <div className="p-4 text-center text-slate-500">読み込み中...</div>
    );
  }

  if (loadError && readOnly) {
    return (
      <div className="p-4 text-sm text-red-600">{loadError}</div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* 分納出荷計画 */}
      <div className="bg-white rounded-lg border border-slate-200">
        {readOnly && splitPlans.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">分納はありません。</div>
        ) : (
          <div className="overflow-y-auto" style={{ maxHeight: '180px' }}>
            <table className="text-sm" style={{ width: 'auto' }}>
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">分納回</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">数量</th>
                  {!hideSupplierResponseDueDate && (
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">予定納期</th>
                  )}
                  {!hideShipmentFields && (
                    <>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">発送日</th>
                      {showArrivalDate && (
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">着日</th>
                      )}
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">発送準備</th>
                    </>
                  )}
                  {!readOnly && (
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 border-b border-slate-200 whitespace-nowrap">操作</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {splitPlans.map((plan, index) => {
                      const isLastRow = index === splitPlans.length - 1 && !addingNew;
                      return (
                        <tr
                          key={plan.split_no}
                          className={editingSplitNo === plan.split_no ? 'bg-blue-50' : 'hover:bg-slate-50'}
                        >
                          <td className="px-3 py-2 text-slate-900 whitespace-nowrap">{plan.split_no}</td>
                          {editingSplitNo === plan.split_no && !readOnly ? (
                            <>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  value={formData.planned_qty || ''}
                                  onChange={(e) => handleChange('planned_qty', e.target.value)}
                                  min="1"
                                  className={`w-20 px-2 py-1 text-sm border rounded ${
                                    errors.planned_qty ? 'border-red-300' : 'border-slate-300'
                                  }`}
                                />
                                {errors.planned_qty && (
                                  <div className="text-xs text-red-600 mt-1">{errors.planned_qty}</div>
                                )}
                              </td>
                              {!hideSupplierResponseDueDate && (
                                <td className="px-3 py-2">
                                  <input
                                    type="date"
                                    value={formData.supplier_response_due_date || ''}
                                    onChange={(e) => handleChange('supplier_response_due_date', e.target.value)}
                                    disabled={hasShipmentData}
                                    className={`w-32 px-2 py-1 text-sm border border-slate-300 rounded ${
                                      hasShipmentData ? 'bg-slate-100 cursor-not-allowed' : ''
                                    }`}
                                  />
                                </td>
                              )}
                              {!hideShipmentFields && (
                                <>
                                  <td className="px-3 py-2">
                                    <input
                                      type="date"
                                      value={formData.shipment_date || ''}
                                      onChange={(e) => handleChange('shipment_date', e.target.value)}
                                      disabled={hasSupplierResponseDueDate}
                                      className={`w-32 px-2 py-1 text-sm border rounded ${
                                        errors.shipment_date ? 'border-red-300' : 'border-slate-300'
                                      } ${
                                        hasSupplierResponseDueDate ? 'bg-slate-100 cursor-not-allowed' : ''
                                      }`}
                                    />
                                    {errors.shipment_date && (
                                      <div className="text-xs text-red-600 mt-1">{errors.shipment_date}</div>
                                    )}
                                  </td>
                                  {showArrivalDate && (
                                    <td className="px-3 py-2">
                                      <input
                                        type="date"
                                        value={formData.arrival_date || ''}
                                        onChange={(e) => handleChange('arrival_date', e.target.value)}
                                        className="w-32 px-2 py-1 text-sm border border-slate-300 rounded"
                                      />
                                    </td>
                                  )}
                                  <td className="px-3 py-2">
                                    <select
                                      value={formData.shipping_preparation || ''}
                                      onChange={(e) => handleChange('shipping_preparation', e.target.value)}
                                      disabled={hasSupplierResponseDueDate}
                                      className={`w-28 px-2 py-1 text-sm border border-slate-300 rounded ${
                                        hasSupplierResponseDueDate ? 'bg-slate-100 cursor-not-allowed' : ''
                                      }`}
                                      style={{ minWidth: '112px' }}
                                    >
                                      {SHIPPING_PREPARATION_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  </td>
                                </>
                              )}
                              {!readOnly && (
                                <td className="px-3 py-2">
                                  <div className="flex gap-1">
                                    <button
                                      onClick={handleSave}
                                      disabled={saving}
                                      className="px-2 py-1 bg-slate-700 text-white text-xs rounded hover:bg-slate-800 disabled:opacity-50"
                                    >
                                      保存
                                    </button>
                                    <button
                                      onClick={handleCancel}
                                      disabled={saving}
                                      className="px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded hover:bg-slate-300 disabled:opacity-50"
                                    >
                                      取消
                                    </button>
                                  </div>
                                </td>
                              )}
                            </>
                          ) : (
                            <>
                              <td className="px-3 py-2">
                                <div className="w-20 px-2 py-1 text-slate-900 whitespace-nowrap">{formatNumber(plan.planned_qty)}</div>
                              </td>
                              {!hideSupplierResponseDueDate && (
                                <td className="px-3 py-2">
                                  <div className="w-32 px-2 py-1 text-slate-900 whitespace-nowrap">{formatDateDisplay(plan.supplier_response_due_date)}</div>
                                </td>
                              )}
                              {!hideShipmentFields && (
                                <>
                                  <td className="px-3 py-2">
                                    <div className="w-32 px-2 py-1 text-slate-900 whitespace-nowrap">{formatDateDisplay(plan.shipment_date)}</div>
                                  </td>
                                  {showArrivalDate && (
                                    <td className="px-3 py-2">
                                      <div className="w-32 px-2 py-1 text-slate-900 whitespace-nowrap">{formatDateDisplay(plan.arrival_date)}</div>
                                    </td>
                                  )}
                                  <td className="px-3 py-2">
                                    <div className="w-28 px-2 py-1 text-slate-900 whitespace-nowrap">{plan.shipping_preparation || '-'}</div>
                                  </td>
                                </>
                              )}
                              {!readOnly && (
                                <td className="px-3 py-2">
                                  <div className="flex gap-1 items-end">
                                    <button
                                      onClick={() => handleEdit(plan)}
                                      className="px-2 py-1 bg-slate-600 text-white text-xs rounded hover:bg-slate-700"
                                    >
                                      編集
                                    </button>
                                    <button
                                      onClick={() => handleDelete(plan.split_no)}
                                      className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                    >
                                      削除
                                    </button>
                                  </div>
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      );
                    })}
                    
                    {/* 新規追加行 */}
                    {!readOnly && addingNew && (
                      <tr className="bg-blue-50">
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">新規</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={formData.planned_qty || ''}
                            onChange={(e) => handleChange('planned_qty', e.target.value)}
                            min="1"
                            className={`w-20 px-2 py-1 text-sm border rounded ${
                              errors.planned_qty ? 'border-red-300' : 'border-slate-300'
                            }`}
                          />
                          {errors.planned_qty && (
                            <div className="text-xs text-red-600 mt-1">{errors.planned_qty}</div>
                          )}
                        </td>
                        {!hideSupplierResponseDueDate && (
                          <td className="px-3 py-2">
                            <input
                              type="date"
                              value={formData.supplier_response_due_date || ''}
                              onChange={(e) => handleChange('supplier_response_due_date', e.target.value)}
                              disabled={hasShipmentData}
                              className={`w-32 px-2 py-1 text-sm border border-slate-300 rounded ${
                                hasShipmentData ? 'bg-slate-100 cursor-not-allowed' : ''
                              }`}
                            />
                          </td>
                        )}
                        {!hideShipmentFields && (
                          <>
                            <td className="px-3 py-2">
                              <input
                                type="date"
                                value={formData.shipment_date || ''}
                                onChange={(e) => handleChange('shipment_date', e.target.value)}
                                disabled={hasSupplierResponseDueDate}
                                className={`w-32 px-2 py-1 text-sm border rounded ${
                                  errors.shipment_date ? 'border-red-300' : 'border-slate-300'
                                } ${
                                  hasSupplierResponseDueDate ? 'bg-slate-100 cursor-not-allowed' : ''
                                }`}
                              />
                              {errors.shipment_date && (
                                <div className="text-xs text-red-600 mt-1">{errors.shipment_date}</div>
                              )}
                            </td>
                            {showArrivalDate && (
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={formData.arrival_date || ''}
                                  onChange={(e) => handleChange('arrival_date', e.target.value)}
                                  className="w-32 px-2 py-1 text-sm border border-slate-300 rounded"
                                />
                              </td>
                            )}
                            <td className="px-3 py-2">
                              <select
                                value={formData.shipping_preparation || ''}
                                onChange={(e) => handleChange('shipping_preparation', e.target.value)}
                                disabled={hasSupplierResponseDueDate}
                                className={`w-28 px-2 py-1 text-sm border border-slate-300 rounded ${
                                  hasSupplierResponseDueDate ? 'bg-slate-100 cursor-not-allowed' : ''
                                }`}
                                style={{ minWidth: '112px' }}
                              >
                                {SHIPPING_PREPARATION_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </td>
                          </>
                        )}
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button
                              onClick={handleSave}
                              disabled={saving}
                              className="px-2 py-1 bg-slate-700 text-white text-xs rounded hover:bg-slate-800 disabled:opacity-50"
                            >
                              保存
                            </button>
                            <button
                              onClick={handleCancel}
                              disabled={saving}
                              className="px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded hover:bg-slate-300 disabled:opacity-50"
                            >
                              取消
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
